import { useEffect, useState } from "react";

import { ErrorAlert, errorCodeOf } from "../components/ErrorAlert";
import { Modal } from "../components/Modal";

import { reportContext } from "./clientLog";

import type { ApiErrorCode, CanvaschoolApi } from "../api/client";
import type { JsonValue, MyClientReport } from "@canvaschool/contracts";

export type FeedbackSource = "USER_REPORT" | "SURVEY";

const COPY: Record<FeedbackSource, { title: string; label: string }> = {
  USER_REPORT: {
    title: "의견·요청 보내기",
    label:
      "바라는 기능, 불편한 점, 오류까지 무엇이든 적어 주세요. 오류라면 무엇을 하다가 그랬는지도 알려 주세요.",
  },
  SURVEY: {
    title: "불편한 점 알려주기",
    label: "쓰면서 불편했던 점이나 바라는 점을 적어 주세요.",
  },
};

const CLOSE_AFTER_SENT_MILLISECONDS = 1_500;

const SENT_AT = new Intl.DateTimeFormat("ko-KR", {
  month: "long",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Asia/Seoul",
});

type HistoryState =
  | { status: "closed" | "loading" }
  | { status: "loaded"; items: MyClientReport[] }
  | { status: "failed"; error: ApiErrorCode };

/** 나의 문의: fetched the first time it is opened, not with every modal. */
const MyReports = ({
  api,
}: {
  api: Pick<CanvaschoolApi, "listMyClientReports">;
}) => {
  const [history, setHistory] = useState<HistoryState>({ status: "closed" });
  const load = () => {
    setHistory({ status: "loading" });
    api.listMyClientReports().then(
      (items) => setHistory({ status: "loaded", items }),
      (failure) =>
        setHistory({ status: "failed", error: errorCodeOf(failure) }),
    );
  };
  return (
    <details
      className="canvaschool-feedback-history"
      onToggle={(event) => {
        if (event.currentTarget.open && history.status !== "loaded") {
          load();
        }
      }}
    >
      <summary>나의 문의 이력</summary>
      {history.status === "loading" && (
        <p className="canvaschool-hint">불러오는 중…</p>
      )}
      {history.status === "failed" && <ErrorAlert code={history.error} />}
      {history.status === "loaded" &&
        (history.items.length === 0 ? (
          <p className="canvaschool-hint">최근 30일 동안 보낸 문의가 없어요.</p>
        ) : (
          <>
            <ol className="canvaschool-feedback-history-list">
              {history.items.map((item) => (
                <li key={item.id}>
                  <time dateTime={item.createdAt}>
                    {SENT_AT.format(new Date(item.createdAt))}
                    {item.source === "SURVEY" && " · 설문"}
                  </time>
                  <p>{item.message}</p>
                  {item.reply && (
                    <div className="canvaschool-feedback-history-reply">
                      <strong>운영팀 답변</strong>
                      <p>{item.reply}</p>
                    </div>
                  )}
                </li>
              ))}
            </ol>
            <p className="canvaschool-hint">
              보낸 문의는 30일 동안 여기에 남습니다.
            </p>
          </>
        ))}
    </details>
  );
};

type FeedbackModalProps = {
  source: FeedbackSource;
  api: Pick<CanvaschoolApi, "sendClientReport" | "listMyClientReports">;
  /** Editor state worth having next to the words (version, pending saves). */
  extraDetail?: () => Record<string, JsonValue>;
  onClose: () => void;
};

/** One text box for both the 의견·요청 button and the survey prompt. */
export const FeedbackModal = ({
  source,
  api,
  extraDetail,
  onClose,
}: FeedbackModalProps) => {
  const [text, setText] = useState("");
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<ApiErrorCode | null>(null);

  useEffect(() => {
    if (!sent) {
      return;
    }
    const timer = window.setTimeout(onClose, CLOSE_AFTER_SENT_MILLISECONDS);
    return () => window.clearTimeout(timer);
  }, [sent, onClose]);

  if (sent) {
    return (
      <Modal
        title={COPY[source].title}
        primaryLabel="닫기"
        hideActions
        onPrimary={onClose}
        onCancel={onClose}
      >
        <p className="canvaschool-success" role="status">
          보냈어요. 꼼꼼히 읽고 반영할게요. 고맙습니다!
        </p>
      </Modal>
    );
  }

  const submit = () => {
    const message = text.trim();
    if (!message) {
      return;
    }
    const context = reportContext();
    setPending(true);
    setError(null);
    api
      .sendClientReport({
        ...context,
        source,
        message,
        detail: { ...context.detail, ...extraDetail?.() },
      })
      .then(
        () => setSent(true),
        (failure) => {
          setError(errorCodeOf(failure));
          setPending(false);
        },
      );
  };

  return (
    <Modal
      title={COPY[source].title}
      formName={COPY[source].title}
      primaryLabel="보내기"
      pending={pending}
      onPrimary={submit}
      onCancel={onClose}
    >
      <div className="canvaschool-form-group">
        <label
          htmlFor="canvaschool-feedback-text"
          className="canvaschool-form-label"
        >
          {COPY[source].label}
        </label>
        <textarea
          id="canvaschool-feedback-text"
          className="canvaschool-feedback-text"
          value={text}
          onChange={(event) => setText(event.target.value)}
          maxLength={2000}
          rows={5}
          required
          autoFocus
        />
        <p className="canvaschool-hint">
          쓴 글은 계정, 기기·브라우저 종류, 최근 오류 기록과 함께 운영자에게
          전달되고 30일 뒤 지워집니다. 이름이나 전화번호는 적지 마세요.{" "}
          <a href="/privacy.html" target="_blank" rel="noreferrer">
            자세히 보기
          </a>
        </p>
      </div>
      {error && <ErrorAlert code={error} />}
      {source === "USER_REPORT" && <MyReports api={api} />}
    </Modal>
  );
};
